import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@frontend-platform/ui-lib/ui';
import { useNavigate } from '@tanstack/react-router';
import { Check, ChevronDown, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { ROUTES } from '@/config/routes';

interface FilterOption {
  value: string;
  label: string;
}

interface FilterDropdownProps {
  label: string;
  values: string[];
  options: FilterOption[];
  onToggle: (value: string) => void;
}

function FilterDropdown({ label, values, options, onToggle }: FilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-background px-2.5 font-medium text-foreground text-sm transition-colors hover:border-zinc-400 hover:bg-muted"
      >
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop closes dropdown on outside click */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
          />
          <div className="absolute top-full left-0 z-20 mt-1.5 min-w-44 rounded-lg border border-border bg-background py-1 shadow-lg">
            {options.length === 0 ? (
              <p className="px-3 py-2 text-muted-foreground text-xs">No options available</p>
            ) : (
              options.map((opt) => {
                const checked = values.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onToggle(opt.value)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-foreground text-sm hover:bg-muted"
                  >
                    <span
                      className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                        checked ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-input'
                      }`}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    {opt.label}
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface ActiveChip {
  label: string;
  onRemove: () => void;
}

interface ActiveFilterChipsProps {
  chips: ActiveChip[];
  onClearAll: () => void;
}

function ActiveFilterChips({ chips, onClearAll }: ActiveFilterChipsProps) {
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((chip) => (
        <span
          key={chip.label}
          className="inline-flex items-center gap-1 rounded-full border border-input bg-background px-2 py-0.5 text-foreground text-xs"
        >
          {chip.label}
          <button
            type="button"
            aria-label={`Remove ${chip.label} filter`}
            onClick={chip.onRemove}
            className="ml-0.5 rounded-full p-0.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="font-medium text-primary text-xs hover:underline"
      >
        Clear all
      </button>
    </div>
  );
}

interface RunListToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  environments: string[];
  onEnvironmentToggle: (value: string) => void;
  environmentOptions: FilterOption[];
  owners: string[];
  onOwnerToggle: (value: string) => void;
  ownerOptions: FilterOption[];
  statuses: string[];
  onStatusToggle: (value: string) => void;
  statusOptions: FilterOption[];
  activeChips: ActiveChip[];
  onClearAllFilters: () => void;
  /** When set, Create Run is disabled and this explains why on hover. */
  createRunDisabledReason?: string;
}

export function RunListToolbar({
  search,
  onSearchChange,
  environments,
  onEnvironmentToggle,
  environmentOptions,
  owners,
  onOwnerToggle,
  ownerOptions,
  statuses,
  onStatusToggle,
  statusOptions,
  activeChips,
  onClearAllFilters,
  createRunDisabledReason,
}: RunListToolbarProps) {
  const navigate = useNavigate();
  const createRunDisabled = !!createRunDisabledReason;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <svg
              className="absolute top-2.5 left-3 h-4 w-4 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <title>Search</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="search"
              placeholder="Search"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="h-8 w-full max-w-xs rounded-lg border border-input bg-background pr-3 pl-9 text-foreground text-sm placeholder:text-muted-foreground focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-300"
            />
          </div>

          <FilterDropdown
            label="Environment"
            values={environments}
            options={environmentOptions}
            onToggle={onEnvironmentToggle}
          />
          <FilterDropdown
            label="Owner"
            values={owners}
            options={ownerOptions}
            onToggle={onOwnerToggle}
          />
          <FilterDropdown
            label="Status"
            values={statuses}
            options={statusOptions}
            onToggle={onStatusToggle}
          />
        </div>

        {/* A disabled button fires no pointer events, so the tooltip needs a
            wrapper as its trigger — otherwise the reason never shows on hover,
            which is the only way the user learns why the action is blocked. */}
        {createRunDisabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button disabled>Create Run</Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{createRunDisabledReason}</TooltipContent>
          </Tooltip>
        ) : (
          <Button onClick={() => navigate({ to: ROUTES.NEW_RUN })}>Create Run</Button>
        )}
      </div>

      <ActiveFilterChips chips={activeChips} onClearAll={onClearAllFilters} />
    </div>
  );
}
